// NDI WebSocket client — 連 NDI 小程序拿即時 frames
//
// Server 在動畫師本機 ws://127.0.0.1:7777（協議見 ndi-helper/README.md）
//   7777 被別的程式占用時，小程序（0.1.15 起）會改用 7778～7787 或 17777 / 27777 / 37777 其中一個 → 沒指定 url 時，
//   連不上 7777 會去那幾個 port 找（清單 NDI_PORTS）「有送 hello 的小程序」，找到的 port 記起來、下次先試它
//   - 連線後 server 送一次 text {"type":"hello",...}，接著 {"type":"status",...}（有變就推 + 每秒一次）
//   - 每幀：binary message = JPEG bytes
//   - 客戶端可送 "status" / {"type":"select_source"} / {"type":"pause"} / {"type":"resume"}
//
// 這層只管 WebSocket + 解 JPEG；「現在卡在哪一步、要不要重連」在 ndiConnection.ts
//
// 用法：
//   const client = new NdiClient({
//     onFrame: (bitmap, w, h) => { /* drawImage / texture upload */ },
//     onStatus: (s) => console.log(s),
//   });
//   client.connect();
//   ...
//   client.disconnect();

export type NdiClientStatus =
  | { kind: 'idle' }
  | { kind: 'connecting'; url: string }
  | { kind: 'connected'; url: string; helperVersion: string; source: string }
  | { kind: 'error'; message: string }
  | { kind: 'disconnected' };

interface Options {
  url?: string;                                       // 預設 ws://127.0.0.1:7777（沒指定才會找 7778～7787）
  /** 等多久還沒連上（握手沒完成）就放棄這一次；回 0 = 不限（瀏覽器可能正在問權限，不能逾時） */
  connectTimeoutMs?: () => number;
  onFrame: (bitmap: ImageBitmap, ts: number) => void; // 每幀
  onStatus?: (s: NdiClientStatus) => void;
  onSources?: (sources: string[]) => void;            // 可用 source 清單（從 status 回應）
  /** 每一則 JSON 訊息（hello / status / select_ack / error）原樣給 */
  onMessage?: (msg: Record<string, unknown>) => void;
  /** 每收到一張 JPEG（還沒解）就叫一次；回 false = 這張不用解（暫停中） */
  onBinary?: () => boolean | void;
  /** 自動 reconnect 間隔（毫秒）；0 = 不重連（交給上層） */
  reconnectIntervalMs?: number;
}

const DEFAULT_URL = 'ws://127.0.0.1:7777';
export const NDI_FIRST_PORT = 7777;
/** 小程序和網頁共用的 port 清單（要跟 ndi-helper/src/ws_server.rs 的 PORT_LIST 一樣）。遠的三個是給 Windows 一次保留一大段 port 的情況 */
export const NDI_PORTS: readonly number[] = [7777, 7778, 7779, 7780, 7781, 7782, 7783, 7784, 7785, 7786, 7787, 17777, 27777, 37777];
/** 握手過了卻一直沒送 hello（不是我們的小程序）：多久放棄 */
const HELLO_TIMEOUT_MS = 4000;
/** 找其他 port 時，整批最多等多久 */
const SCAN_TIMEOUT_MS = 1500;

const urlOfPort = (port: number) => `ws://127.0.0.1:${port}`;

const PORT_STORAGE_KEY = 'sp_ndi_port';
type PortStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
function safeLocal(): PortStorage | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
}
/** 這台電腦上次連上的 port；不在清單裡的值不算 */
export function readStoredNdiPort(storage: Pick<Storage, 'getItem'> | null = safeLocal()): number | null {
  try {
    const n = Number(storage?.getItem(PORT_STORAGE_KEY));
    return NDI_PORTS.includes(n) ? n : null;
  } catch { return null; }
}
export function storeNdiPort(port: number | null, storage: PortStorage | null = safeLocal()) {
  try {
    // 7777 本來就先試，不用記
    if (port === null || port === NDI_FIRST_PORT) storage?.removeItem(PORT_STORAGE_KEY);
    else storage?.setItem(PORT_STORAGE_KEY, String(port));
  } catch { /* 無痕模式 → 不記，下次多找一輪 */ }
}

/** 上一次連上小程序的 port（所有連線共用；重連先試它）。存在這台電腦，下次開頁直接連，不用再找一輪 */
let rememberedPort: number | null = readStoredNdiPort();
export function getRememberedNdiPort(): number | null { return rememberedPort; }
export function resetRememberedNdiPort() { rememberedPort = null; storeNdiPort(null); }

/** 是我們的小程序送的 hello 嗎（別的程式剛好也開 WebSocket 不算） */
export function isHelperHello(raw: unknown): boolean {
  if (typeof raw !== 'string') return false;
  try {
    const m = JSON.parse(raw);
    return !!m && typeof m === 'object' && m.type === 'hello' && typeof m.version === 'string';
  } catch {
    return false;
  }
}

/** 連續第 n 次連不上時，要不要順便找其他 port：第 2 次，之後每 3 次一回（安靜、不頻繁） */
export function shouldScanPorts(misses: number): boolean {
  return misses === 2 || (misses > 2 && misses % 3 === 0);
}

export class NdiClient {
  private url: string;
  /** 呼叫端有指定 url → 只連那個，不找別的 port */
  private fixedUrl: boolean;
  private ws: WebSocket | null = null;
  private opts: Options;
  private status: NdiClientStatus = { kind: 'idle' };
  private reconnectTimer: number | null = null;
  private destroyed = false;
  // 上一幀如果還在 decode，新幀進來就丟掉（節流防止記憶體爆掉）
  private decoding = false;
  /** 連續沒連上（沒收到 hello）的次數 */
  private misses = 0;
  private scanToken = 0;
  private scanSockets: WebSocket[] = [];
  private attemptTimer: number | null = null;

  constructor(opts: Options) {
    this.url = opts.url || DEFAULT_URL;
    this.fixedUrl = !!opts.url;
    this.opts = opts;
  }

  getStatus(): NdiClientStatus { return this.status; }

  connect() {
    if (this.destroyed) return;
    this.disconnect();
    // 預設：先試上次連上的 port，沒有就 7777
    const port = this.fixedUrl ? null : (rememberedPort ?? NDI_FIRST_PORT);
    this.openSocket(port === null ? this.url : urlOfPort(port), port);
  }

  private openSocket(url: string, port: number | null) {
    this.url = url;
    this.setStatus({ kind: 'connecting', url });
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      this.setStatus({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
      this.attemptFailed(port);
      return;
    }
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    let gotHello = false;

    const clearAttemptTimer = () => {
      if (this.attemptTimer !== null) {
        window.clearTimeout(this.attemptTimer);
        this.attemptTimer = null;
      }
    };
    /** 這次嘗試放棄（逾時）：關掉，當作連不上 */
    const giveUp = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.attemptTimer = null;
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      try { ws.close(); } catch { /* ignore */ }
      this.attemptFailed(port);
    };
    const armTimer = (ms: number) => {
      clearAttemptTimer();
      if (ms > 0) this.attemptTimer = window.setTimeout(giveUp, ms);
    };
    armTimer(this.opts.connectTimeoutMs?.() ?? 0);
    ws.onopen = () => {
      if (this.ws !== ws) return;
      armTimer(HELLO_TIMEOUT_MS);  // 握手過了卻沒有 hello → 不是我們的小程序
    };

    ws.onmessage = async (ev) => {
      if (this.ws !== ws) return;  // 舊的連線（已經換新的了）
      if (typeof ev.data === 'string') {
        let msg: Record<string, unknown>;
        try {
          const parsed = JSON.parse(ev.data);
          if (!parsed || typeof parsed !== 'object') return;
          msg = parsed as Record<string, unknown>;
        } catch {
          return;  // 看不懂的不理
        }
        if (msg.type === 'hello') {
          gotHello = true;
          clearAttemptTimer();
          this.misses = 0;
          if (port !== null) { rememberedPort = port; storeNdiPort(port); }
          const source = typeof msg.source === 'string' && msg.source ? msg.source : '(no source)';
          this.setStatus({ kind: 'connected', url: this.url, helperVersion: String(msg.version ?? ''), source });
          // 舊版小程序不會自己送 status → 連上之後問一次（新版會回一則，無害）
          this.send('status');
        } else if (msg.type === 'status') {
          if (Array.isArray(msg.availableSources)) {
            this.opts.onSources?.(msg.availableSources.filter((s): s is string => typeof s === 'string'));
          }
          if (typeof msg.source === 'string' && msg.source && this.status.kind === 'connected') {
            this.setStatus({ ...this.status, source: msg.source });
          }
        }
        this.opts.onMessage?.(msg);
      } else {
        // Binary = JPEG bytes
        if (this.opts.onBinary?.() === false) return;
        if (this.decoding) return;          // 跳過上一幀沒解完的
        this.decoding = true;
        try {
          const blob = new Blob([ev.data as ArrayBuffer], { type: 'image/jpeg' });
          const bitmap = await createImageBitmap(blob);
          if (this.ws === ws) this.opts.onFrame(bitmap, performance.now());
          else bitmap.close?.();
        } catch {
          /* 壞掉的一張就跳過 */
        } finally {
          this.decoding = false;
        }
      }
    };

    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      clearAttemptTimer();
      if (gotHello) this.emitDisconnected();
      else this.attemptFailed(port);
    };

    ws.onerror = () => {
      if (this.ws !== ws) return;
      this.setStatus({ kind: 'error', message: '連不到 NDI 小程序' });
      // onclose 會接著觸發 → reconnect
    };
  }

  private emitDisconnected() {
    this.setStatus({ kind: 'disconnected' });
    this.scheduleReconnect();
  }

  /**
   * 這一次沒連上（還沒收到 hello）。該順便找其他 port 的時候先找，找到就直接連過去（畫面不閃）；
   * 找不到才回報斷線（上層照原本的退避重試，7777 永遠先試）
   */
  private attemptFailed(port: number | null) {
    this.misses += 1;
    if (this.fixedUrl || port === null) {
      this.emitDisconnected();
      return;
    }
    // 上次連上的不是 7777、這次連不到了 → 馬上找一輪（小程序可能重開換了 port）
    const lostRemembered = rememberedPort === port && port !== NDI_FIRST_PORT;
    if (lostRemembered) { rememberedPort = null; storeNdiPort(null); }
    if (!lostRemembered && !shouldScanPorts(this.misses)) {
      this.emitDisconnected();
      return;
    }
    const token = ++this.scanToken;
    void this.scanPorts(port, token).then((found) => {
      if (this.destroyed || token !== this.scanToken) return;
      if (found !== null) this.openSocket(urlOfPort(found), found);
      else this.emitDisconnected();
    });
  }

  /** 同時試清單裡其他的 port（扣掉剛試過的），第一個送 hello 的就是；都沒有 → null */
  private scanPorts(skip: number, token: number): Promise<number | null> {
    return new Promise((resolve) => {
      const ports: number[] = [];
      for (const p of NDI_PORTS) if (p !== skip) ports.push(p);
      let pending = ports.length;
      let done = false;
      const sockets: WebSocket[] = [];
      let timer: number | null = null;
      const finish = (result: number | null) => {
        if (done) return;
        done = true;
        if (timer !== null) window.clearTimeout(timer);
        for (const s of sockets) {
          s.onopen = s.onmessage = s.onclose = s.onerror = null;
          try { s.close(); } catch { /* ignore */ }
        }
        if (this.scanToken === token) this.scanSockets = [];
        resolve(result);
      };
      timer = window.setTimeout(() => finish(null), SCAN_TIMEOUT_MS);
      for (const p of ports) {
        let s: WebSocket;
        try {
          s = new WebSocket(urlOfPort(p));
        } catch {
          if (--pending === 0) finish(null);
          continue;
        }
        sockets.push(s);
        let counted = false;
        const miss = () => {
          if (counted) return;
          counted = true;
          if (--pending === 0) finish(null);
        };
        s.onmessage = (ev) => { if (isHelperHello(ev.data)) finish(p); };
        s.onerror = miss;
        s.onclose = miss;
      }
      if (this.scanToken === token) this.scanSockets = sockets;
    });
  }

  disconnect() {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.attemptTimer !== null) {
      window.clearTimeout(this.attemptTimer);
      this.attemptTimer = null;
    }
    // 找其他 port 找到一半 → 取消
    this.scanToken += 1;
    for (const s of this.scanSockets) {
      s.onopen = s.onmessage = s.onclose = s.onerror = null;
      try { s.close(); } catch { /* ignore */ }
    }
    this.scanSockets = [];
    const ws = this.ws;
    if (ws) {
      this.ws = null;
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      try { ws.close(); } catch { /* ignore */ }
    }
  }

  destroy() {
    this.destroyed = true;
    this.disconnect();
  }

  isOpen(): boolean {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /** 送 text frame 給 helper */
  send(text: string) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.send(text); } catch { /* swallow */ }
    }
  }

  /** 拉一次最新 status（含 availableSources） */
  requestStatus() { this.send('status'); }

  /** 切換 NDI source；null = 回到自動挑（這台電腦的 AE 優先） */
  selectSource(name: string | null) {
    this.send(JSON.stringify({ type: 'select_source', name }));
  }

  /** 這條連線先不要畫面（小程序沒人要畫面時就不解碼） */
  pause() { this.send(JSON.stringify({ type: 'pause' })); }

  /** 恢復收畫面 */
  resume() { this.send(JSON.stringify({ type: 'resume' })); }

  private setStatus(s: NdiClientStatus) {
    this.status = s;
    this.opts.onStatus?.(s);
  }

  private scheduleReconnect() {
    if (this.destroyed) return;
    const delay = this.opts.reconnectIntervalMs ?? 3000;
    if (delay <= 0) return;
    if (this.reconnectTimer !== null) return;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}
